import { ReactElement, useMemo } from 'react'
import { reject, reverse, sortBy, toNumber } from 'lodash-es'
import { Stack } from '@mui/material'
import { storeyToLabel } from '@editor/pages/Plans/components/AddPlanDialog/components/utils'
import { DividerHeading, TwoColumnGrid } from '@ui2/structure'
import { CheckboxLabeled } from '@ui/actions'
import { roofStoreyKey, useModelStore } from '@editorStores'
import type { PlanTapelineConfig, PlanType } from 'src/domain/planTables/types'

const planTypeOptions: { value: PlanType; label: string }[] = [
  { value: 'PositionPlan', label: 'Positionsplan' },
  { value: 'StiffeningPlan', label: 'Aussteifungsplan' },
  { value: 'LoadPlan', label: 'Lastplan' },
]

interface Props {
  planConfig: PlanTapelineConfig
  onChange: (planConfig: PlanTapelineConfig) => void
}

const PlanExportConfigFields = ({ planConfig, onChange }: Props): ReactElement => {
  const availableStoreys = useModelStore(state => state.availableStoreys)

  const sortedStoreys = useMemo(() => {
    const nonRoof = reject(Array.from(availableStoreys), storey => storey === roofStoreyKey)
    return [roofStoreyKey, ...reverse(sortBy(nonRoof, storey => toNumber(storey)))]
  }, [availableStoreys])

  const isStoreySelected = (storey: string): boolean => planConfig.storeysForPlans.includes(storey)

  const toggleStoreyForPlans = (storey: string): void => {
    const isSelected = isStoreySelected(storey)
    const nextStoreys = isSelected
      ? planConfig.storeysForPlans.filter(value => value !== storey)
      : [...planConfig.storeysForPlans, storey]

    onChange({
      ...planConfig,
      storeysForPlans: nextStoreys,
    })
  }

  const togglePlanType = (planType: PlanType): void => {
    const nextPlanTypes = planConfig.planTypes.includes(planType)
      ? planConfig.planTypes.filter(value => value !== planType)
      : [...planConfig.planTypes, planType]

    onChange({
      ...planConfig,
      planTypes: nextPlanTypes,
    })
  }

  return (
    <Stack spacing={1} data-cy="plan-table-export-settings">
      <Stack spacing={0.25}>
        <DividerHeading spacingTop={0}>Stockwerke für Planerstellung</DividerHeading>
        <TwoColumnGrid fillColumnsFirst>
          {sortedStoreys.map(storey => (
            <CheckboxLabeled
              key={storey}
              checked={isStoreySelected(storey)}
              onChange={() => toggleStoreyForPlans(storey)}
              label={storeyToLabel(storey)}
              data-cy={`plan-table-storey-${storey}`}
            />
          ))}
        </TwoColumnGrid>
      </Stack>

      <Stack spacing={0.25}>
        <DividerHeading spacingTop={0}>In Plänen anzeigen</DividerHeading>
        <Stack>
          {planTypeOptions.map(({ value, label }) => (
            <CheckboxLabeled
              key={value}
              checked={planConfig.planTypes.includes(value)}
              onChange={() => togglePlanType(value)}
              label={label}
              data-cy={`plan-table-plan-type-${value}`}
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  )
}

export default PlanExportConfigFields
